import { ApiService } from '../../api/api-service';
import { PaginatedResponse, SortParams, User, UserFilters } from '../../types';
import { sanitizeFilters } from '../../utils/filter-sanitizer';

const SERVICE_ROOT = '/usermanagement';

/**
 * Service for user-related API operations
 */
export class UserService extends ApiService {
  /**
   * Get a paginated list of users
   * @param page Page number
   * @param pageSize Number of items per page
   * @param filters Optional filters
   * @param sort Optional sort parameters
   * @returns Promise resolving to the paginated user data
   */
  async getUsers(
    page: number,
    pageSize: number,
    filters?: UserFilters,
    sort?: SortParams
  ): Promise<PaginatedResponse<User>> {
    return this.handleApiCall(async () => {
      // Sanitize filters: remove undefined/null/empty and convert known booleans
      const apiFilters = filters ? sanitizeFilters(filters as Record<string, unknown>) : {};

      return this.client.get(SERVICE_ROOT, {
        params: {
          page,
          pageSize,
          ...apiFilters,
          ...sort,
        },
      });
    }, 'Error fetching users');
  }

  /**
   * Get a user by ID
   * @param id User ID
   * @returns Promise resolving to the user data
   */
  async getUserById(id: string): Promise<User> {
    return this.handleApiCall(async () => {
      return this.client.get(`${SERVICE_ROOT}/${id}`);
    }, `Error fetching user with ID ${id}`);
  }

  /**
   * Create a new user
   * @param user User data
   * @param avatarFile The optional avatar image
   * @returns Promise resolving to the created user
   */
  async createUser(user: Omit<User, 'id'>, avatarFile?: File): Promise<User> {
    return this.handleApiCall(async () => {
      const formData = new FormData();

      Object.entries(user).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      return this.client.post(SERVICE_ROOT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }, 'Error creating user');
  }

  /**
   * Update a user
   * @param id User ID
   * @param User User data to update
   * @param avatarFile The optional avatar image
   * @returns Promise resolving to the updated user
   */
  async updateUser(id: string, User: Partial<User>, avatarFile?: File): Promise<void> {
    return this.handleApiCall(async () => {
      const formData = new FormData();

      // Add all user fields to the form data
      Object.entries(User).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      return this.client.put(`${SERVICE_ROOT}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }, 'Error updating user');
  }

  /**
   * Delete a user
   * @param id User ID
   * @returns Promise resolving when the user is deleted
   */
  async deleteUser(id: string): Promise<void> {
    return this.handleApiCall(async () => {
      return this.client.delete(`${SERVICE_ROOT}/${id}`);
    }, `Error deleting user with ID ${id}`);
  }
}
